import React from 'react'
import { formBuilder } from '../../pages/telaDinamica/form-builder'
import { normalizeComponentLabel } from '../../pages/telaDinamica/form-builder-utils'
import { EditorFiltrosUnidades } from './EditorFiltrosUnidades'
import { dataLocal, filtrosValidos, semUnidadeDisponivel } from './padraoNaTela'
import { carregarUnidadesDoUsuario, paraIdLabel } from './telaFixaAgendavel'

const PREFIXO_DATA = 'dt_'
const CAMPO_UNIDADE = 'oid_unidade'

/** Mesmo criterio do formBuilder e do back: campo de data e o que comeca com dt_. */
export const isCampoData = (campo) => typeof campo === 'string' && campo.startsWith(PREFIXO_DATA)

/** Os campos do documento.parametros (JSON); [] se vazio ou invalido. */
export const lerParametros = (parametros) => {
  if (!parametros) {
    return []
  }
  try {
    const campos = JSON.parse(parametros)
    return Array.isArray(campos) ? campos : []
  } catch (e) {
    return []
  }
}

/** Linhas da grade de datas do dialogo: os campos dt_* do documento, com o rotulo que a tela mostra. */
export const camposDataDoDocumento = (parametros) =>
  lerParametros(parametros)
    .filter((info) => info && info.componente !== 'hidden' && isCampoData(info.campo))
    .map((info) => ({ campo: info.campo, label: normalizeComponentLabel(info.campo) }))

/**
 * A tela dinamica guarda a unidade escolhida como { id, label } (e a forcada pelo topo como { id, value }); o
 * EditorFiltrosUnidades trabalha com { id, value }. Sem isto o nome da unidade se perde ao editar a lista.
 */
export const unidadesComoIdValue = (unidades) =>
  Array.isArray(unidades)
    ? unidades.map((u) => ({ id: u.id, value: u.value !== undefined && u.value !== null ? u.value : u.label }))
    : unidades

// no dialogo a unidade nao segue a unidade escolhida no topo da tela (LtsMultiselectUnidade a forcaria e trocaria
// as unidades salvas de quem so editou o e-mail): trava so para usuario de unidade unica, como no DRE
const componentes = {
  [CAMPO_UNIDADE]: ({ record, onChange, label }) => (
    <div className="p-grid form-group" key={CAMPO_UNIDADE}>
      <EditorFiltrosUnidades
        filtros={{ ...record, [CAMPO_UNIDADE]: unidadesComoIdValue(record[CAMPO_UNIDADE]) }}
        onChange={onChange}
        campo={CAMPO_UNIDADE}
        label={label}
      />
    </div>
  ),
}

// um editor por documento.parametros: o componente tem de ser o mesmo entre renders do dialogo, senao o React
// remonta o formulario a cada tecla e o campo de texto perde o foco
const editores = new Map()

/**
 * Editor dos filtros que nao sao data no AgendarRelatorioDialog: o proprio formulario da tela dinamica, sem os
 * campos dt_* (que vao para a grade de datas). null se o documento nao tiver outro campo.
 */
export const editorFiltrosDoDocumento = (parametros) => {
  const temOutroCampo = lerParametros(parametros).some(
    (info) => info && info.componente !== 'hidden' && !isCampoData(info.campo)
  )
  if (!temOutroCampo) {
    return null
  }
  if (!editores.has(parametros)) {
    const EditorFiltrosTelaDinamica = ({ filtros, onChange }) => (
      <div className="p-col-12 agendar-relatorio-filtros-tela">
        {formBuilder({
          parametros,
          record: filtros,
          messages: [],
          setField: onChange,
          omitir: isCampoData,
          componentes,
        })}
      </div>
    )
    editores.set(parametros, EditorFiltrosTelaDinamica)
  }
  return editores.get(parametros)
}

/**
 * Filtro padrao (PHOENIX-107, fase 3) da tela dinamica: o registro gravado de volta, com as datas em Date local. So a
 * unidade (oid_unidade) e conferida contra as opcoes de agora (as unidades que o usuario acessa); as demais listas
 * (usuario, produto...) dependem do componente de cada campo e vao como gravadas: item que nao existe mais so nao traz
 * linhas.
 *
 * @param camposData os campos dt_* do documento (camposDataDoDocumento)
 * @return Promise<{ record, descartados, naoAplicado }>; a unidade no record como a tela a guarda ({ id, label });
 *         naoAplicado quando nenhuma unidade gravada esta mais disponivel (ver aplicarPreparado)
 */
export const prepararPadraoTelaDinamica = (filtros, camposData, carregarUnidades = carregarUnidadesDoUsuario) => {
  const gravados = filtros || {}
  const gravadas = gravados[CAMPO_UNIDADE]
  const opcoes =
    Array.isArray(gravadas) && gravadas.length > 0 ? carregarUnidades().catch(() => null) : Promise.resolve(null)
  return opcoes.then((unidades) => {
    const { filtros: validos, descartados } = filtrosValidos(gravados, [
      { filtro: CAMPO_UNIDADE, chave: 'id', opcoes: unidades },
    ])
    if (semUnidadeDisponivel(gravadas, validos[CAMPO_UNIDADE])) {
      return { record: {}, descartados, naoAplicado: true }
    }
    const datas = new Set((camposData || []).map(({ campo }) => campo))
    const record = {}
    Object.keys(validos).forEach((campo) => {
      if (campo === CAMPO_UNIDADE && Array.isArray(validos[campo])) {
        record[campo] = paraIdLabel(validos[campo])
      } else if (!datas.has(campo)) {
        record[campo] = validos[campo]
      } else if (dataLocal(validos[campo])) {
        record[campo] = dataLocal(validos[campo])
      }
    })
    return { record, descartados }
  })
}

/**
 * O que o AgendarRelatorioDialog precisa para um relatorio da tela dinamica.
 *
 * @param documento { oid, nome, nomeRelatorio, parametros }
 */
export const getTelaDinamicaAgendavel = (documento) => {
  if (!documento || !documento.oid) {
    return null
  }
  return {
    relatorio: { path: documento.nomeRelatorio, oidDocumento: documento.oid, descricao: documento.nome },
    camposData: camposDataDoDocumento(documento.parametros),
    EditorFiltros: editorFiltrosDoDocumento(documento.parametros),
  }
}
